import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  QrCode, X, Camera, Keyboard, Package, Monitor, Truck,
  Wrench, Archive, ShoppingCart, Clock, FileText, ClipboardList,
  ScrollText, ArrowRight, History, Search,
} from 'lucide-react';

const STATUS_COLORS = {
  Available: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  Deployed: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  'Under Repair': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  Retired: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  'In Transit': 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  Sold: 'bg-red-500/15 text-red-400 border-red-500/30',
  Active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
};

const STATUS_ICONS = {
  Available: <Package className="h-4 w-4" />,
  Deployed: <Monitor className="h-4 w-4" />,
  'Under Repair': <Wrench className="h-4 w-4" />,
  'In Transit': <Truck className="h-4 w-4" />,
  Retired: <Archive className="h-4 w-4" />,
  Sold: <ShoppingCart className="h-4 w-4" />,
};

const EVENT_LABELS = {
  QC_DISPATCH: 'QC Dispatch',
  QC_RETURN: 'QC Return',
  DELIVERY: 'Delivery',
  PICKUP: 'Pickup',
  UPGRADE: 'Upgrade',
  DOWNGRADE: 'Downgrade',
  REPAIR_IN: 'Sent to Repair',
  REPAIR_OUT: 'Repair Complete',
  SOLD: 'Sold',
};

const QRScannerDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('camera'); // 'camera' | 'manual'
  const [manualId, setManualId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);

  // Camera scanner setup
  useEffect(() => {
    if (!open || mode !== 'camera') return;

    let html5Qr = null;
    let isRunning = false;
    const startScanner = async () => {
      try {
        setCameraError(false);
        html5Qr = new Html5Qrcode('qr-reader-container');
        scannerRef.current = html5Qr;

        await html5Qr.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 220, height: 220 }, aspectRatio: 1.0 },
          (decodedText) => {
            isRunning = false;
            html5Qr.stop().catch(() => {});
            scannerRef.current = null;
            lookupAsset(decodedText);
          },
          () => {} // ignore scan errors
        );
        isRunning = true;
      } catch (err) {
        console.warn('Camera unavailable:', err);
        setCameraError(true);
        isRunning = false;
      }
    };

    // Small delay to let DOM mount
    const timer = setTimeout(startScanner, 300);

    return () => {
      clearTimeout(timer);
      if (isRunning && scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
      scannerRef.current = null;
    };
  }, [open, mode]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      setResult(null);
      setManualId('');
      setMode('camera');
      setCameraError(false);
      scannerRef.current = null;
    }
  }, [open]);

  const lookupAsset = async (assetId) => {
    setLoading(true);
    try {
      const res = await api.get(`/assets/lookup/${assetId}`);
      setResult(res.data);
      toast.success(`Asset found: ${assetId}`);
    } catch (err) {
      toast.error(`Asset not found: ${assetId}`);
      setResult(null);
      // Re-enable scanner after failed lookup
      if (mode === 'camera') {
        setMode('manual'); // switch to manual on failure
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualLookup = () => {
    const id = manualId.trim();
    if (!id) { toast.error('Enter an Asset ID'); return; }
    lookupAsset(id);
  };

  const handleAction = (action) => {
    onClose();
    const assetId = result?.asset?.asset_id;
    switch (action) {
      case 'qc':
        navigate(`/qc/new?asset=${assetId}`);
        break;
      case 'history':
        navigate(`/history?asset=${assetId}`);
        break;
      case 'contract':
        if (result?.current_contract?.id) {
          navigate(`/contracts/${result.current_contract.id}`);
        } else {
          navigate('/contracts');
        }
        break;
      case 'ledger':
        navigate(`/inventory`);
        break;
      default:
        break;
    }
  };

  const handleScanAgain = () => {
    setResult(null);
    setManualId('');
    setMode('camera');
  };

  const asset = result?.asset;
  const status = asset ? (asset.asset_status === 'Active' ? 'Available' : asset.asset_status) : '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <QrCode className="h-5 w-5 text-primary" />
            {result ? 'Asset Found' : 'Scan Asset QR Code'}
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 pb-4 space-y-4">
          {/* Scanner / Input Mode (when no result) */}
          {!result && !loading && (
            <>
              {/* Mode Tabs */}
              <div className="flex gap-1 bg-muted rounded-lg p-1">
                <button
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${mode === 'camera' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
                  onClick={() => setMode('camera')}
                  data-testid="qr-camera-tab"
                >
                  <Camera className="h-4 w-4" />Camera
                </button>
                <button
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${mode === 'manual' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
                  onClick={() => setMode('manual')}
                  data-testid="qr-manual-tab"
                >
                  <Keyboard className="h-4 w-4" />Manual
                </button>
              </div>

              {/* Camera Scanner */}
              {mode === 'camera' && (
                <div className="space-y-3">
                  {cameraError ? (
                    <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-8 text-center space-y-3">
                      <Camera className="h-10 w-10 mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Camera access denied or unavailable</p>
                      <Button variant="outline" size="sm" onClick={() => setMode('manual')}>
                        <Keyboard className="h-4 w-4 mr-2" />Enter ID manually
                      </Button>
                    </div>
                  ) : (
                    <div className="relative rounded-lg overflow-hidden bg-black">
                      <div id="qr-reader-container" className="w-full" ref={containerRef} />
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        Point camera at the QR code on the asset
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Manual Entry */}
              {mode === 'manual' && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g. RC-LAP-00001"
                      value={manualId}
                      onChange={e => setManualId(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleManualLookup()}
                      className="font-mono"
                      autoFocus
                      data-testid="qr-manual-input"
                    />
                    <Button onClick={handleManualLookup} data-testid="qr-lookup-btn">
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter the asset ID printed on the QR sticker
                  </p>
                </div>
              )}
            </>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
              <p className="text-sm text-muted-foreground">Looking up asset...</p>
            </div>
          )}

          {/* Asset Result Panel */}
          {result && asset && (
            <div className="space-y-4" data-testid="qr-result-panel">
              {/* Asset Header */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-lg">{asset.asset_id}</span>
                        <Badge variant="outline" className={STATUS_COLORS[status]}>
                          {STATUS_ICONS[status]} <span className="ml-1">{status}</span>
                        </Badge>
                      </div>
                      <div className="text-sm font-medium">{asset.brand} {asset.model_name}</div>
                      <div className="text-xs text-muted-foreground">{asset.asset_type} &middot; SN: {asset.serial_number}</div>
                      {asset.current_customer && (
                        <div className="text-xs text-blue-400 flex items-center gap-1">
                          <Monitor className="h-3 w-3" /> Deployed to: {asset.current_customer}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      {asset.default_monthly_rate && (
                        <div className="text-sm font-medium">₹{asset.default_monthly_rate}/mo</div>
                      )}
                      {asset.ram_size && <div className="text-xs text-muted-foreground">RAM: {asset.ram_size}</div>}
                      {asset.ssd_size && <div className="text-xs text-muted-foreground">SSD: {asset.ssd_size}</div>}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Current Contract */}
              {result.current_contract && (
                <Card className="border-blue-500/30">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <ScrollText className="h-4 w-4 text-blue-400" />
                      <span className="font-mono font-medium">{result.current_contract.contract_number}</span>
                      <span className="text-muted-foreground">&middot;</span>
                      <span>{result.current_contract.customer_name}</span>
                      <Badge variant="outline" className="text-xs">{result.current_contract.status}</Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Activity */}
              {(result.ledger || []).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Recent Activity</span>
                  </div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {result.ledger.slice(0, 5).map((entry, i) => (
                      <div key={entry.id || i} className="flex items-center gap-2 text-xs p-2 rounded bg-muted/50">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {EVENT_LABELS[entry.event_type] || entry.event_type}
                        </Badge>
                        {entry.customer_name && <span className="text-muted-foreground">{entry.customer_name}</span>}
                        <span className="ml-auto text-muted-foreground">
                          {new Date(entry.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent QC */}
              {(result.recent_qcs || []).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ClipboardList className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Last QC</span>
                  </div>
                  <div className="text-xs p-2 rounded bg-muted/50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={result.recent_qcs[0].qc_status === 'PASS' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}>
                        {result.recent_qcs[0].qc_status}
                      </Badge>
                      <span>Score: {result.recent_qcs[0].qc_score}</span>
                      <span className="text-muted-foreground">by {result.recent_qcs[0].created_by_technician}</span>
                    </div>
                    <span className="text-muted-foreground">{new Date(result.recent_qcs[0].created_timestamp).toLocaleDateString()}</span>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button variant="default" className="h-auto py-3" onClick={() => handleAction('qc')} data-testid="qr-action-qc">
                  <div className="flex flex-col items-center gap-1">
                    <ClipboardList className="h-5 w-5" />
                    <span className="text-xs">Start QC</span>
                  </div>
                </Button>
                <Button variant="outline" className="h-auto py-3" onClick={() => handleAction('history')} data-testid="qr-action-history">
                  <div className="flex flex-col items-center gap-1">
                    <History className="h-5 w-5" />
                    <span className="text-xs">QC History</span>
                  </div>
                </Button>
                <Button variant="outline" className="h-auto py-3" onClick={() => handleAction('contract')} data-testid="qr-action-contract">
                  <div className="flex flex-col items-center gap-1">
                    <ScrollText className="h-5 w-5" />
                    <span className="text-xs">{result.current_contract ? 'View Contract' : 'Contracts'}</span>
                  </div>
                </Button>
                <Button variant="outline" className="h-auto py-3" onClick={() => handleAction('ledger')} data-testid="qr-action-ledger">
                  <div className="flex flex-col items-center gap-1">
                    <FileText className="h-5 w-5" />
                    <span className="text-xs">Inventory</span>
                  </div>
                </Button>
              </div>

              {/* Scan Again */}
              <Button variant="ghost" className="w-full" onClick={handleScanAgain} data-testid="qr-scan-again">
                <QrCode className="h-4 w-4 mr-2" />Scan Another Asset
              </Button>
            </div>
          )}

          {/* Close button when no result */}
          {!result && !loading && (
            <Button variant="ghost" className="w-full text-muted-foreground" onClick={onClose}>
              <X className="h-4 w-4 mr-2" />Close
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRScannerDialog;
